import { useState, useEffect, createContext, useContext } from 'react';
import { backofficeAuthService, BackofficeUser, BackofficeLoginRequest } from '../services/backofficeAuthService';

interface BackofficeAuthContextType {
  user: BackofficeUser | null;
  loading: boolean;
  login: (credentials: BackofficeLoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  refreshUser: () => Promise<void>;
}

export const BackofficeAuthContext = createContext<BackofficeAuthContextType | undefined>(undefined);

export function useBackofficeAuthProvider() {
  const [user, setUser] = useState<BackofficeUser | null>(null);
  const [loading, setLoading] = useState(true);

  const checkAuthStatus = async () => {
    try {
      if (backofficeAuthService.isAuthenticated()) {
        const userData = await backofficeAuthService.getCurrentUser();
        setUser(userData);
      }
    } catch (error) {
      console.warn('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const login = async (credentials: BackofficeLoginRequest) => {
    setLoading(true);
    try {
      const response = await backofficeAuthService.login(credentials);
      setUser(response.user);
    } catch (error) {
      setUser(null);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    setLoading(true);
    try {
      await backofficeAuthService.logout();
    } catch (error) {
      console.warn('Logout failed:', error);
    } finally {
      setUser(null);
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    try {
      if (backofficeAuthService.isAuthenticated()) {
        const userData = await backofficeAuthService.getCurrentUser();
        setUser(userData);
      }
    } catch (error) {
      console.warn('User refresh failed:', error);
      setUser(null);
    }
  };

  const isAuthenticated = !!user && backofficeAuthService.isAuthenticated();

  return {
    user,
    loading,
    login,
    logout,
    isAuthenticated,
    refreshUser,
  };
}

export function useBackofficeAuth() {
  const context = useContext(BackofficeAuthContext);
  if (context === undefined) {
    throw new Error('useBackofficeAuth must be used within a BackofficeAuthProvider');
  }
  return context;
}